import { z } from 'zod/v4';

/** Shared so the create side can restate it while carrying its own missing-field message. */
export const LOGO_URL_DESCRIPTION =
    'Logo image source. An absolute https:// URL is used as-is; a bare path is resolved against the ' +
    "SendPulse file-manager CDN, so only pass one if it's a real file-manager path.";

export const logoUrlSchema = z.string().min(1).describe(LOGO_URL_DESCRIPTION);

/**
 * THE logo widget data shape as AI tools accept it — a FLAT projection of the real entity's two
 * levels, same reasoning as indent's `height`/image's `url`: the real shape nests a ~40-field
 * file-manager pick under `data.image` and puts sizing on the envelope's `styles`, and a model can
 * only legitimately author the one field it actually has an opinion about.
 *
 * `url` is the ONLY field lifted out of `data.image` — unlike the `image` widget, whose view also
 * reads `alt`/`link`/`imageWidth`/`imageHeight`, `logo`'s view only ever reads `image?.url`
 * (confirmed by reading `logo-view.component.ts`/`.html`: everything else the type once had is
 * commented out in `logo-widget.type.ts`). Exposing those fields here would be plumbing with no
 * widget behind it.
 */
export const partialLogoWidgetDataSchema = z.object({
    // --- source: folded into `data.image` ---
    url: logoUrlSchema.optional(),

    // --- appearance: folded into the envelope's `styles` ---
    maxWidth: z
        .number()
        .int()
        .min(1)
        .max(5000)
        .optional()
        .describe('Maximum rendered width in PIXELS (not a percentage). Defaults to 100.'),
    borderRadius: z
        .number()
        .int()
        .min(0)
        .max(500)
        .optional()
        .describe('Corner rounding in pixels, applied to all four corners. Use a large value for a circle.'),
});

export type PartialLogoWidgetData = z.infer<typeof partialLogoWidgetDataSchema>;

/** The one flat key that belongs to `data.image` rather than `data` — shared with the mutation layer. */
export const LOGO_SOURCE_KEYS = ['url'] as const;

/** The flat keys that belong to the envelope's `styles` group rather than `data`. */
export const LOGO_STYLE_KEYS = ['maxWidth', 'borderRadius'] as const;
